import { useEffect, useRef, useState } from "react";
import WaveSurfer from "wavesurfer.js";
import { ReactComponent as PlayIcon } from "../../../../assets/icons/play.svg";
import { ReactComponent as PauseIcon } from "../../../../assets/icons/pause.svg";
import { useApp } from "../../../../services/app-context";


/* =========================================================
   Format time in MM:SS
========================================================= */
function formatTime(seconds: number): string {
  if (!isFinite(seconds) || isNaN(seconds)) return "00:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
}

/* =========================================================
   Main Player Component
========================================================= */
export default function StickWaveAudioPlayer({ audioUrl }: { audioUrl: string }) {
  const { styles } = useApp();

  const AUDIO_URL = audioUrl;
  const progressColor = styles?.colors?.PraxisTerracotta || styles?.colors?.primary3 || "#000000";

  const waveformRef = useRef<HTMLDivElement>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);

  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* -------------------------------------------------------
     Initialize WaveSurfer when audio URL changes
  ------------------------------------------------------- */
  useEffect(() => {
    if (!waveformRef.current) {
      return;
    }

    if (!AUDIO_URL) {
      setError("No audio URL available");
      setIsLoading(false);
      return;
    }

    // Destroy existing instance if any
    if (wavesurferRef.current) {
      wavesurferRef.current.destroy();
      wavesurferRef.current = null;
    }

    setIsLoading(true);
    setError(null);
    try {
      // Create WaveSurfer instance
      const wavesurfer = WaveSurfer.create({
        container: waveformRef.current,
        waveColor: "#A1A1AA",
        progressColor,
        cursorColor: "transparent",
        barWidth: 3,
        barRadius: 1.5,
        barGap: 2,
        height: 19,
        normalize: true,
        backend: "WebAudio",
        mediaControls: false,
        interact: true,
      });

      wavesurferRef.current = wavesurfer;

      // Event listeners
      wavesurfer.on("ready", () => {
        setIsLoading(false);
        const dur = wavesurfer.getDuration();
        setDuration(dur);
        setError(null);
      });

      wavesurfer.on("play", () => {
        setPlaying(true);
      });

      wavesurfer.on("pause", () => {
        setPlaying(false);
        // WebAudio backend can emit a stale time (e.g. 0) around pause; sync from the player after pause completes.
        setCurrentTime(wavesurfer.getCurrentTime());
      });

      wavesurfer.on("timeupdate", () => {
        setCurrentTime(wavesurfer.getCurrentTime());
      });

      wavesurfer.on("seeking", (time: number) => {
        setCurrentTime(time);
      });

      wavesurfer.on("finish", () => {
        setPlaying(false);
      });

      wavesurfer.on("error", (error: any) => {
        if (error.name !== "AbortError") {
          setIsLoading(false);
          setError(`Failed to load audio: ${error?.message || "Unknown error"}`);
        }
      });

      // Load audio
      wavesurfer.load(AUDIO_URL).catch((err: any) => {
        if (err.name !== "AbortError") {
          setIsLoading(false);
          setError(`Failed to load audio: ${err?.message || "Unknown error"}`);
        }
      });
    } catch (err: any) {
      setIsLoading(false);
      setError(`Failed to initialize player: ${err?.message || "Unknown error"}`);
    }

    // Cleanup
    return () => {
      if (wavesurferRef.current) {
        wavesurferRef.current?.pause();
        wavesurferRef.current.destroy();
        wavesurferRef.current = null;
      }
    };
  }, [AUDIO_URL]);

  /* -------------------------------------------------------
     Sync progress color without destroying the instance
  ------------------------------------------------------- */
  useEffect(() => {
    if (wavesurferRef.current) {
      wavesurferRef.current.setOptions({ progressColor });
    }
  }, [progressColor]);

  /* -------------------------------------------------------
     Controls
  ------------------------------------------------------- */
  const togglePlay = () => {
    if (wavesurferRef.current) {
      wavesurferRef.current.playPause();
    }
  };

  const seekBy = (sec: number) => {
    if (wavesurferRef.current && duration > 0) {
      const current = wavesurferRef.current.getCurrentTime();
      const newTime = Math.max(0, Math.min(current + sec, duration));
      wavesurferRef.current.seekTo(newTime / duration);
    }
  };


  /* -------------------------------------------------------
     Render
  ------------------------------------------------------- */
  return (
    <div className="rounded-md border-[0.5px] border-[#ECECEC] bg-gray-100 p-2 flex items-start gap-4 ">
        <div className="flex items-center gap-4">
          {/* Attempt Dropdown */}
          

          {/* Controls */}
          <div className="flex items-center gap-3 flex-1">
            {/* <button
              onClick={() => seekBy(-10)}
              disabled={isLoading || !AUDIO_URL || !wavesurferRef.current || !!error}
              className="flex flex-col items-center gap-1 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <BackwardIcon className="w-[22px] h-[22px]" />
            </button> */}

            <button
              onClick={togglePlay}
              disabled={isLoading || !AUDIO_URL || !wavesurferRef.current || !!error}
              className="flex items-center justify-center hover:opacity-80 bg-PraxisTerracotta rounded-full w-6 h-6 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {playing ? (
                <PauseIcon className="w-3 h-3 [&_path]:fill-white" />
              ) : (
                <PlayIcon className="w-3 h-3 [&_path]:fill-white" />
              )}
            </button>

            {/* <button
              onClick={() => seekBy(10)}
              disabled={isLoading || !AUDIO_URL || !wavesurferRef.current || !!error}
              className="flex flex-col items-center gap-1 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ForwardIcon className="w-[22px] h-[22px]" />
            </button> */}
          </div>

        </div>

        <div className="flex flex-col gap-2 flex-1">

          {/* Waveform */}
          <div className="relative flex-1 bg-white w-full desktop5:max-w-full rounded-full py-1 px-2 overflow-hidden min-h-6">
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-xs z-10">
                Loading...
              </div>
            )}
            {error && !isLoading && (
              <div className="absolute inset-0 flex items-center justify-center text-red-400 text-xs z-10">
                Failed to load audio
              </div>
            )}
            {!AUDIO_URL && !isLoading && (
              <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-xs z-10">
                No audio URL available
              </div>
            )}
            <div ref={waveformRef} className="min-w-[150px] w-full desktop5:w-full min-h-4" />
          </div>
          {/* Current time / total duration */}
          <div className="text-xs font-medium tracking-[0.9px] text-text-gray-500 tabular-nums whitespace-nowrap">
            {duration > 0
              ? <><span className="!text-PraxisTerracotta">{formatTime(currentTime)}</span> / {formatTime(duration)}</>
              : isLoading
                ? "…"
                : "00:00"}
          </div>
        </div>

    </div>
  );
}
